/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useCallback, useEffect, useRef, useState, type MemoExoticComponent } from 'react';

import { cssMap, cx, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import type { FieldBaseProps } from '../common/types';
import { useValidateRequiredField } from '../hooks/useValidateRequiredField';

import type {
	JiraNumberField_Platform$data,
	JiraNumberField_Platform$key,
} from './__generated__/JiraNumberField_Platform.graphql';
import { ValidationPopup } from './ValidationPopup';

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_CURRENCY_DISPLAY = 'symbol';

type JiraNumberFieldFormatConfig = JiraNumberField_Platform$data['formatConfig'];

type IntlWithSupportedValues = typeof Intl & {
	supportedValuesOf?: (key: string) => string[];
};

// Number formatting utils, based on jira/src/packages/platform/utils/number/src/index.tsx
const isSupportedCurrencyCode = (currencyCode: string): boolean => {
	const intlMaybe = Intl as IntlWithSupportedValues;
	return (
		typeof intlMaybe.supportedValuesOf === 'function' ? intlMaybe.supportedValuesOf('currency') : []
	).includes(currencyCode);
};

const formatCurrency = (
	number: number,
	locale: string,
	currency: Intl.NumberFormatOptions['currency'] = DEFAULT_CURRENCY,
): string => {
	if (isSupportedCurrencyCode(currency)) {
		return new Intl.NumberFormat(locale, {
			style: 'currency',
			currency,
			currencyDisplay: DEFAULT_CURRENCY_DISPLAY,
			maximumFractionDigits: Number.isInteger(number) ? 0 : undefined,
		}).format(number);
	}

	return formatDecimal(number, locale);
};

export const transformToUiValue = (
	number: number | null | undefined,
	formatConfig: JiraNumberFieldFormatConfig,
): number | null | undefined =>
	number != null && formatConfig?.formatStyle === 'PERCENT' ? number * 100 : number;

export const transformToServerValue = (
	number: number | null | undefined,
	formatConfig: JiraNumberFieldFormatConfig,
): number | null => {
	if (number == null || formatConfig?.formatStyle !== 'PERCENT') {
		return number ?? null;
	}

	// Use Number.EPSILON to avoid floating-point precision issues
	const result = number / 100;
	const factor = Math.pow(10, 8); // 8 decimal places should be sufficient for percentage precision
	return Math.round((result + Number.EPSILON) * factor) / factor;
};

const formatPercent = (number: number, locale: string): string =>
	new Intl.NumberFormat(locale, {
		style: 'unit',
		unit: 'percent',
	}).format(number);

const formatDecimal = (number: number, locale: string): string =>
	new Intl.NumberFormat(locale, {
		style: 'decimal',
	}).format(number);

const formatNumberField = (
	number: number,
	locale: string,
	formatConfig: JiraNumberFieldFormatConfig,
): string => {
	if (formatConfig == null) {
		return formatDecimal(number, locale);
	}

	const { formatStyle, formatUnit } = formatConfig;

	switch (formatStyle) {
		case 'CURRENCY':
			return formatUnit
				? formatCurrency(number, locale, formatUnit)
				: formatDecimal(number, locale);
		case 'PERCENT':
			return formatPercent(number, locale);
		default:
			return formatDecimal(number, locale);
	}
};

const messages = defineMessages({
	requiredMessage: {
		id: 'jira.jira-platform-fields.jira-number-field.required-message',
		defaultMessage: '{fieldName} is required',
		description: 'Validation message when required number field is empty',
	},
	none: {
		id: 'jira.jira-platform-fields.number-field.none',
		defaultMessage: 'None',
		description: 'Display text shown when no value has been set for the field',
	},
});

const styles = cssMap({
	textFieldContainer: {
		width: '100%',
		// The TextField component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	editableTextFieldReadViewContainer: {
		width: '100%',
		height: '40px',
	},
	textField: {
		width: '100%',
	},
	readView: {
		height: '40px',
		alignItems: 'center',
		display: 'flex',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	editableReadView: {
		cursor: 'pointer',
		height: '36px', // 40px - 2 * 2px outline
	},
});

export type Props = FieldBaseProps & {
	field: JiraNumberField_Platform$key;
	onChange: (newValue: number | null) => void;
};

/**
 * `JiraNumberField` is a React component that renders a Jira number field,
 * supporting both editable and read-only modes. It displays the field value and allows
 * users to edit the numeric value if the field is editable. The component uses
 * Relay fragments to fetch the current value.
 *
 * @param field - The Relay fragment reference for the Jira number field.
 * @param fieldValue - The current numeric value.
 * @param onChange - Callback invoked when the number value changes.
 */
export const JiraNumberField: MemoExoticComponent<
	({ fieldValue, field, isFieldEditable, onChange }: Props) => JSX.Element
> = memo(({ fieldValue, field, isFieldEditable = true, onChange }: Props): JSX.Element => {
	const data = useFragment<JiraNumberField_Platform$key>(
		graphql`
			fragment JiraNumberField_Platform on JiraNumberField {
				__typename
				name
				fieldConfig {
					isEditable
					isRequired
				}
				number
				formatConfig {
					formatStyle
					formatUnit
				}
			}
		`,
		field,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage, locale } = useIntl();
	const [isEditing, setIsEditing] = useState(false);

	const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;
	const isRequired = !!data?.fieldConfig?.isRequired;
	const defaultValue = data.number;
	const currentValue = transformToUiValue(
		typeof fieldValue === 'number' ? fieldValue : null,
		data.formatConfig,
	);
	const hasValue = currentValue != null;
	const displayText = hasValue
		? formatNumberField(currentValue, locale, data.formatConfig)
		: formatMessage(messages.none);

	// Allows us to make sure we only fall back to default value once, on initial render
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		// If the passed down value is empty and a default value is available, tell the upstream
		if (fieldValue == null && defaultValue != null && isFirstRenderRef.current) {
			onChange(transformToServerValue(defaultValue, data.formatConfig));
		}
		isFirstRenderRef.current = false;
	}, [fieldValue, hasValue, onChange, defaultValue, data.formatConfig]);

	const validate = useValidateRequiredField({
		isRequired,
		isEditable,
		fieldName: data.name,
		fieldTypeForAnalytics: data.__typename,
	});

	const onEdit = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'initiated',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName: data.name,
				fieldType: data.__typename,
				isRequired,
				isEditable,
			},
		);
		setIsEditing(true);
	}, [createAnalyticsEvent, data.name, data.__typename, isRequired, isEditable]);

	const onCancel = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'cancelled',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName: data.name,
				fieldType: data.__typename,
				isRequired,
				isEditable,
			},
		);
		setIsEditing(false);
	}, [createAnalyticsEvent, data.name, data.__typename, isRequired, isEditable]);

	const onConfirm = useCallback(
		(newValue: string) => {
			setIsEditing(false);
			const newNumericValue = newValue === '' ? null : Number(newValue);

			if (newNumericValue !== currentValue) {
				onChange(transformToServerValue(newNumericValue, data.formatConfig));
			} else {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'cancelled',
						actionSubject: 'inlineEdit',
					}),
					{
						fieldName: data.name,
						fieldType: data.__typename,
						isRequired,
						isEditable,
					},
				);
			}
		},
		[
			createAnalyticsEvent,
			onChange,
			currentValue,
			data.formatConfig,
			data.name,
			data.__typename,
			isRequired,
			isEditable,
		],
	);

	const readView = useCallback(() => {
		return (
			<Box xcss={cx(styles.readView, isEditable && styles.editableReadView)}>
				<Text maxLines={1} color={hasValue ? 'color.text' : 'color.text.subtle'}>
					{displayText}
				</Text>
			</Box>
		);
	}, [displayText, isEditable, hasValue]);

	if (!isEditable) {
		return readView();
	}

	return (
		<Box
			xcss={cx(styles.textFieldContainer, !isEditing && styles.editableTextFieldReadViewContainer)}
		>
			<InlineEdit
				isEditing={isEditing}
				defaultValue={currentValue}
				editButtonLabel={displayText}
				editView={({ errorMessage, isInvalid, ...fieldProps }) => (
					<ValidationPopup
						showInvalidPopup={isInvalid}
						errorMessage={isInvalid ? errorMessage : undefined}
						trigger={(triggerProps) => (
							<Box xcss={styles.textField}>
								<Textfield
									{...fieldProps}
									{...triggerProps}
									type="number"
									step="any"
									autoFocus
									isInvalid={isInvalid}
									aria-invalid={isInvalid}
									aria-describedby={isInvalid ? triggerProps.errorId : undefined}
									aria-errormessage={isInvalid ? errorMessage : undefined}
								/>
							</Box>
						)}
					/>
				)}
				readView={readView}
				onConfirm={onConfirm}
				onCancel={onCancel}
				onEdit={onEdit}
				readViewFitContainerWidth
				validate={validate}
			/>
		</Box>
	);
});
